import React, { useCallback, useEffect, useRef, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';

import AvtalegiroDetailsHeader from '../../components/AvatlegiroDetailsHeader/AvtalegiroDetailsHeader';
import { TransactionForm, TransactionSubmitWrapper } from '../../components/TransactionForm';
import AvtalegiroUpdateInfo from '../../components/transaction/AvtaleGiro/AvtalegiroUpdateInfo';
import DeleteAvtaleGiro from '../../components/transaction/AvtaleGiro/DeleteAvtaleGiro';
import { AvtaleGiroFields } from '../../components/transaction/AvtaleGiro/fields';
import { AvtaleGiroActionType } from '../../components/transaction/AvtaleGiro/form/actions';
import createAvtalegiroUpdateRequest from '../../components/transaction/AvtaleGiro/form/createAvtalegiroUpdateRequest';
import avtalegiroReducer from '../../components/transaction/AvtaleGiro/form/reducer';
import { getInitialState } from '../../components/transaction/AvtaleGiro/form/state';
import useFormContext, { AvtalegiroFormContext } from '../../components/transaction/AvtaleGiro/form/useFormContext';
import useHasChanged from '../../components/transaction/AvtaleGiro/form/useHasChanged';
import validate from '../../components/transaction/AvtaleGiro/form/validate';
import { hasSigningError } from '../../components/transaction/Payment/PaymentForm';
import { SelectedReceiver } from '../../components/transaction/SelectedReceiver';
import UpdateRetry from '../../components/transaction/UpdateRetry';
import { useConfig } from '../../defaultConfig';
import type { AccountId, AvtalegiroAgreementDetails, PaymentAgreementId } from '../../generated-types';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import AvtalegiroDetailsFooter from './AvtalegiroDetailsFooter';

interface Props {
    avtalegiro: AvtalegiroAgreementDetails;
    id: PaymentAgreementId;
    toAccount?: SelectedReceiver;
    onClose: () => void;
    isBlocked: boolean;
    onDeleted: () => void;
}

const dateFormat = 'dd. MMMM yyyy hh:mm';

const AvtaleGiroDetailsForm: React.FC<Props> = ({ avtalegiro, toAccount, id, onClose, isBlocked, onDeleted }) => {
    const { state, handleChange, checkFormValidity } = useFormContext();

    const lastPaymentDate = avtalegiro?.lastPaymentDate
        ? format(getZonedDate(avtalegiro?.lastPaymentDate), dateFormat)
        : undefined;

    const { signingOperationPending, signingStatus } = useSigningContext();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const {
        featureToggles: { disableAvtaleGiroChanges },
    } = useConfig();
    const fromAccountId = useRef<AccountId>();
    const { startSigning, signingError, showPaymentRetry, handleCancelSigningFlow } = useSigningContext(
        createAvtalegiroUpdateRequest(),
    );

    useEffect(() => {
        if (toAccount) {
            handleChange({
                type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED,
                payload: toAccount,
            });
        }
    }, [toAccount, handleChange]);

    const hasStateChange = useHasChanged(avtalegiro);

    const updateAvtalegiroAgreement = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning({ ...state, id });
            }
        },
        [checkFormValidity, state, startSigning, id],
    );

    if (showPaymentRetry && !showDeleteModal) {
        return <UpdateRetry onClose={onClose} avtalegiro />;
    }

    if (signingError || signingStatus === 'COMPLETED' || hasSigningError(signingStatus)) {
        return (
            <AvtalegiroUpdateInfo
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId.current}
                onClose={onClose}
                isDeleteOperation={showDeleteModal}
            />
        );
    }

    if (showDeleteModal) {
        return (
            <div className="delete-avtalegiro__result">
                <DeleteAvtaleGiro
                    showDeleteDialogue={showDeleteModal}
                    closeDeleteDialogue={() => setShowDeleteModal(false)}
                    id={id}
                    onDeleted={onDeleted}
                />
            </div>
        );
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <AvtalegiroDetailsHeader lastPaymentDate={lastPaymentDate} />
            <TransactionForm onSubmit={updateAvtalegiroAgreement} doubleAmountFields>
                <AvtaleGiroFields lockKid lockReceiver lockAll={disableAvtaleGiroChanges} />
                <TransactionSubmitWrapper className="avtalegiro__footer">
                    <AvtalegiroDetailsFooter
                        paymentOperationPending={signingOperationPending}
                        hasStateChange={hasStateChange}
                        isBlocked={isBlocked}
                        onDelete={() => setShowDeleteModal(true)}
                    />
                </TransactionSubmitWrapper>
            </TransactionForm>
        </SigningContextFlow>
    );
};

export default (props: Props) => (
    <AvtalegiroFormContext
        validateHook={validate}
        reducer={avtalegiroReducer}
        initialState={getInitialState(props.avtalegiro, props.toAccount)}
    >
        <AvtaleGiroDetailsForm {...props} />
    </AvtalegiroFormContext>
);
